import { CustomListItemId } from "@sp-crm/core";
import { GetCustomListQuery } from "generated/graphql";

export type ClientStatusGroup =
    | "all"
    | "leads"
    | "activeClients"
    | "closedLost"
    | "previousClients";

export const clientStatusGroupOptions: {
    value: ClientStatusGroup;
    label: string;
}[] = [
    { value: "all", label: "All statuses" },
    { value: "leads", label: "Current leads" },
    { value: "activeClients", label: "Active clients" },
    { value: "closedLost", label: "Lost leads" },
    { value: "previousClients", label: "Former clients" },
];

export type HomeCareStatusList = GetCustomListQuery["getCustomList"] | null;

const activeClientStatusKeys: string[] = ["hcClosedWon"];
const previousClientStatusKeys: string[] = ["hcClosedPreviousClient"];

function categoryIds(customList: NonNullable<HomeCareStatusList>, key: string) {
    return customList.categories
        .filter(category => category.key === key)
        .map(category => category.id);
}

export function statusIdsForGroup(
    customList: HomeCareStatusList,
    group: ClientStatusGroup,
): CustomListItemId[] {
    if (!customList) {
        return [];
    }
    if (group === "all") {
        return customList.items.map(item => item.id);
    }
    if (group === "leads") {
        const activeIds = categoryIds(customList, "Active");
        return customList.items
            .filter(item => activeIds.includes(item.customListCategoryId))
            .map(item => item.id);
    }
    if (group === "activeClients") {
        return customList.items
            .filter(item => activeClientStatusKeys.includes(item.key))
            .map(item => item.id);
    }
    if (group === "previousClients") {
        return customList.items
            .filter(item => previousClientStatusKeys.includes(item.key))
            .map(item => item.id);
    }
    const closedIds = categoryIds(customList, "Closed");
    return customList.items
        .filter(item => closedIds.includes(item.customListCategoryId))
        .filter(
            item =>
                !activeClientStatusKeys.includes(item.key) &&
                !previousClientStatusKeys.includes(item.key),
        )
        .map(item => item.id);
}

export function availableClientStatusGroupOptions(
    customList: HomeCareStatusList,
): { value: ClientStatusGroup; label: string }[] {
    return clientStatusGroupOptions.filter(
        option =>
            option.value === "all" ||
            statusIdsForGroup(customList, option.value).length > 0,
    );
}

export function statusIdsForSelection(
    customList: HomeCareStatusList,
    selectedStatusIds: CustomListItemId[],
): CustomListItemId[] {
    if (selectedStatusIds.length > 0) {
        return selectedStatusIds;
    }
    return statusIdsForGroup(customList, "all");
}

export function groupForStatusIds(
    customList: HomeCareStatusList,
    selectedStatusIds: CustomListItemId[],
): ClientStatusGroup | null {
    if (!customList) {
        return null;
    }
    if (selectedStatusIds.length === 0) {
        return "all";
    }
    const selected = new Set(selectedStatusIds);
    const matchingOption = clientStatusGroupOptions.find(option => {
        const groupIds = statusIdsForGroup(customList, option.value);
        return (
            groupIds.length === selected.size &&
            groupIds.every(groupId => selected.has(groupId))
        );
    });
    return matchingOption ? matchingOption.value : null;
}
